import React, { useEffect, useState } from 'react';
import { formatDay } from './billingService';
import { useBillingStore } from './billingStore';
import { PayDialog } from './PayDialog';

interface BillingBannerProps {
  teamId: string;
}

/** 看板顶部的提示条：只在「宽限中」和「只读」时出现；所有者/管理员能直接点去购买或续费 */
export const BillingBanner: React.FC<BillingBannerProps> = ({ teamId }) => {
  const billing = useBillingStore(s => s.byTeam[teamId]);
  const pricing = useBillingStore(s => s.pricing);
  const load = useBillingStore(s => s.load);
  const [paying, setPaying] = useState(false);

  useEffect(() => { load(teamId); }, [load, teamId]);

  if (!billing) return null;

  // 付款成功后团队恢复可写，提示条会消失；但支付弹窗要留着把「已到账」显示完，所以弹窗不挂在提示条的显示条件里
  const visible = billing.state === 'GRACE' || billing.state === 'READONLY';

  const readonly = billing.state === 'READONLY';
  const renewing = billing.activePaidSeats > 0 || !!billing.paidUntil;
  const missing = Math.max(1, billing.seatsNeeded - billing.activePaidSeats);

  let message: string;
  if (readonly) {
    message = `团队 ${billing.memberCount} 人，超过免费的 ${billing.freeSeats} 人，席位已到期，团队暂时只读（可以查看，不能新建和修改）。`;
  } else if (billing.inPaidGrace) {
    message = `付费席位已于 ${formatDay(billing.paidUntil)} 到期，${formatDay(billing.graceEndsAt)} 前续费，否则团队将变为只读。`;
  } else {
    message = `团队 ${billing.memberCount} 人，超过免费的 ${billing.freeSeats} 人。${formatDay(billing.graceEndsAt)} 前购买 ${missing} 个席位，否则团队将变为只读。`;
  }

  return (
    <>
      {visible && <div
        className={`mb-6 px-4 py-3 rounded-lg border flex items-center justify-between gap-4 text-sm ${
          readonly ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-900'
        }`}
        role="status"
      >
        <span>{message}</span>
        {billing.canManage ? (
          <button
            type="button"
            onClick={() => setPaying(true)}
            className={`shrink-0 px-3 py-1.5 rounded-md text-white ${readonly ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}
          >
            {renewing ? '续费' : '购买席位'}
          </button>
        ) : (
          <span className="shrink-0 text-xs opacity-80">联系团队所有者购买</span>
        )}
      </div>}

      {paying && (
        <PayDialog
          teamId={teamId}
          intent={renewing ? 'RENEW' : 'BUY_SEATS'}
          defaultSeats={missing}
          pricing={pricing}
          mock={billing.mock}
          onClose={() => setPaying(false)}
          onPaid={() => load(teamId)}
        />
      )}
    </>
  );
};
